export const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "ttclid",
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type AttributionParams = Partial<Record<AttributionKey, string>>;

const ATTRIBUTION_STORAGE_KEY = "proc360_attribution";

function compactAttribution(params: AttributionParams): AttributionParams {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => typeof value === "string" && value.trim() !== ""),
  ) as AttributionParams;
}

export function getAttributionFromSearchParams(searchParams: URLSearchParams): AttributionParams {
  return compactAttribution(
    Object.fromEntries(
      ATTRIBUTION_KEYS.map((key) => [key, searchParams.get(key) || undefined]),
    ) as AttributionParams,
  );
}

export function getStoredAttribution(): AttributionParams {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};

    return compactAttribution(JSON.parse(raw) as AttributionParams);
  } catch {
    return {};
  }
}

export function persistAttribution(params: AttributionParams) {
  if (typeof window === "undefined") return;

  const compactParams = compactAttribution(params);
  if (!Object.keys(compactParams).length) return;

  window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(compactParams));
}

export function captureCurrentAttribution(): AttributionParams {
  if (typeof window === "undefined") return {};

  const currentAttribution = getAttributionFromSearchParams(
    new URLSearchParams(window.location.search),
  );

  if (Object.keys(currentAttribution).length) {
    persistAttribution(currentAttribution);
    return currentAttribution;
  }

  return getStoredAttribution();
}

export function appendAttributionToUrl(url: string, attribution = captureCurrentAttribution()) {
  if (!Object.keys(attribution).length) return url;

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://proc360.app";
  const targetUrl = new URL(url, baseUrl);

  Object.entries(attribution).forEach(([key, value]) => {
    if (typeof value === "string" && value.trim() !== "") {
      targetUrl.searchParams.set(key, value);
    }
  });

  return targetUrl.toString();
}

export function isUserAppUrl(url: string) {
  const userAppBase = process.env.NEXT_PUBLIC_USER_APP;
  if (!userAppBase) return false;

  try {
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://proc360.app";
    const targetUrl = new URL(url, baseUrl);
    const userAppUrl = new URL(userAppBase);

    return targetUrl.origin === userAppUrl.origin;
  } catch {
    return false;
  }
}
